import { useMemo, useRef, type MouseEvent, type PointerEvent } from "react";
import type { EngagementFeedItem } from "../../hooks/useEngagementFeed";
import { resolveSupportedLocale } from "../../lib/locale";
import { useLocalization } from "../../lib/i18n";
import { pulseLabelForPlayoffInsightType } from "./playoffInsightLocalization";

type PlayoffInsightPanelProps = {
  items: EngagementFeedItem[];
  isLoading?: boolean;
  isError?: boolean;
  locale?: string | null;
  onRetry?: () => void;
  onTeamTagClick?: (item: EngagementFeedItem, teamCode: string) => void;
};

function normalizeWhitespace(value: string | null | undefined) {
  return (value ?? "").replace(/\s+/g, " ").trim();
}

function truncateToCharLimit(value: string, maxChars: number) {
  if (value.length <= maxChars) return value;
  const safeLimit = Math.max(0, maxChars - 1);
  return `${value.slice(0, safeLimit).trimEnd()}...`;
}

function getCardCopy(item: EngagementFeedItem) {
  const headline = normalizeWhitespace(item.headline);
  return {
    headline: truncateToCharLimit(headline, 50),
  };
}

function isPulseFeedItem(item: EngagementFeedItem) {
  return (
    item.type === "RESULT" ||
    item.type === "YESTERDAY_RECAP" ||
    item.type === "UPCOMING_GAME" ||
    item.type === "SCHEDULE"
  );
}

const insightCardClassName =
  "flex h-[42px] w-[calc(100vw-4.5rem)] max-w-[32rem] flex-none snap-start items-center gap-3 overflow-hidden rounded-[10px] border border-border-subtle bg-background-card px-[14px] py-[10px] sm:w-[min(32rem,calc(100vw-7rem))] md:w-[min(32rem,calc(100vw-11rem))]";
const insightCarouselClassName =
  "playoff-insight-scrollbar flex min-w-0 cursor-grab snap-x snap-mandatory gap-3 overflow-x-auto pr-6 select-none active:cursor-grabbing";

const DESKTOP_DRAG_THRESHOLD_PX = 6;

function useDesktopCarouselDrag() {
  const dragStateRef = useRef<{
    pointerId: number;
    startX: number;
    startScrollLeft: number;
    didDrag: boolean;
  } | null>(null);
  const suppressClickRef = useRef(false);

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;

    const target = event.currentTarget;
    if (target.scrollWidth <= target.clientWidth) return;

    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: target.scrollLeft,
      didDrag: false,
    };
    target.setPointerCapture(event.pointerId);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - dragState.startX;
    if (Math.abs(deltaX) >= DESKTOP_DRAG_THRESHOLD_PX) {
      dragState.didDrag = true;
      suppressClickRef.current = true;
    }

    if (dragState.didDrag) {
      event.preventDefault();
      event.currentTarget.scrollLeft = dragState.startScrollLeft - deltaX;
    }
  };

  const endPointerDrag = (event: PointerEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) return;

    dragStateRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!suppressClickRef.current) return;

    suppressClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  };

  return {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: endPointerDrag,
    onPointerCancel: endPointerDrag,
    onClickCapture: handleClickCapture,
  };
}

export function PlayoffInsightPanel({
  items,
  locale,
}: PlayoffInsightPanelProps) {
  const { locale: activeLocale } = useLocalization();
  const resolvedLocale = resolveSupportedLocale(locale ?? activeLocale);
  const cards = useMemo(
    () => items.filter(isPulseFeedItem).map((item) => ({ item, copy: getCardCopy(item) })),
    [items]
  );
  const carouselDragProps = useDesktopCarouselDrag();

  if (cards.length === 0) return null;

  return (
    <section className="min-w-0" lang={resolvedLocale} aria-label="Competition pulse">
      <div className={insightCarouselClassName} {...carouselDragProps}>
        {cards.map(({ item, copy }) => (
          <article key={item.id} className={insightCardClassName}>
            <span className="inline-flex h-[18px] shrink-0 items-center rounded-[6px] bg-[#EF9F27] px-2 pt-px font-display text-[11px] font-bold uppercase leading-none tracking-[0.04em] text-[#412402]">
              {pulseLabelForPlayoffInsightType(item.type, resolvedLocale)}
            </span>
            <h4 className="min-w-0 flex-1 truncate text-[13px] font-semibold leading-[18px] text-foreground">
              {copy.headline}
            </h4>
          </article>
        ))}
      </div>
      {cards.length > 1 ? (
        <div className="mt-1.5 flex h-3 items-center justify-center gap-[5px]" aria-hidden="true">
          {cards.map(({ item }, index) => (
            <span
              key={`pulse-dot-${item.id}`}
              className={`h-1.5 w-1.5 rounded-full ${index === 0 ? "bg-[#EF9F27]" : "bg-[#333333]"}`}
            />
          ))}
        </div>
      ) : null}
    </section>
  );
}
